/**
 * Slug → user-facing brand name + friendly verb mapping for every Ordo chat
 * surface. Turns `tasker.list_my_tasks` into "Looking up your Tasks…" and
 * `Bash` into "Running a command…", so a non-technical user never sees a raw
 * tool slug.
 *
 * Mirrors the server-side `agent.brand-names` STYLE skill (seeded by
 * `apps/agent/prisma/defaults.ts`). Source of truth for brand names is the HQ
 * sidebar; URLs ride along for completeness — the chat already passes
 * server-supplied URLs through unchanged, so these are for status text only.
 *
 * [PROD-1044] This module is the ONE copy. It previously existed three times —
 * `apps/agent/client/src/lib/brand-names.ts`, a lifted
 * `packages/agent-embed/src/lib/format-tool-result.ts`, and a third
 * `summarizeToolArgs` inline in the app's `chat.tsx` — and the embed's copy had
 * already drifted: it was missing `NATIVE_TOOL_LABELS` / `poolToolLabel`
 * entirely, so every factory-pool harness tool rendered as "Calling Bash…",
 * exactly the raw-slug leak FACTORY-264 set out to prevent. Both chat surfaces
 * now import from here. Do NOT re-lift this into an app or the embed; add new
 * labels here and every surface gets them.
 *
 * Pure string logic, no framework imports — deliberately its own subpath
 * (`@ordo/agent-tools/tool-status`) so browser bundles don't pull in the
 * package's Express/Node surface.
 */

export interface BrandEntry {
  brand: string
  url: string
}

/** Slug prefix → brand metadata. */
export const BRAND_BY_SLUG_PREFIX: Record<string, BrandEntry> = {
  tasker: { brand: 'Tasks', url: 'https://tasks.ordo.com' },
  boombox: { brand: 'Communications', url: 'https://comms.ordo.com' },
  road_warrior: { brand: 'Schedule', url: 'https://schedule.ordo.com' },
  rfp: { brand: 'RFPs', url: 'https://rfps.ordo.com' },
  hq: { brand: 'HQ', url: 'https://hq.ordo.com' },
  factory: { brand: 'Factory', url: 'https://factory.ordo.com' },
  agent: { brand: 'Agent', url: 'https://agent.ordo.com' },
  skills: { brand: 'Skills', url: 'https://agent.ordo.com/skills' },
}

export function brandForSlug(slug: string): BrandEntry | undefined {
  const prefix = slug.split('.')[0]
  return prefix ? BRAND_BY_SLUG_PREFIX[prefix] : undefined
}

/**
 * [FACTORY-264] Friendly labels for the factory-pool (headless Claude Code)
 * agent's own tools. The pool chat hides raw tool cards, so these drive ONLY
 * the live activity banner. Native harness tools are matched by exact name;
 * the five `factory.*` harness features + workspace MCP tools are matched by
 * substring so both wire forms work (`factory.submit_plan` and the namespaced
 * `mcp__factory__submit_plan`).
 */
const NATIVE_TOOL_LABELS: Record<string, string> = {
  Bash: 'Running a command…',
  Read: 'Reading files…',
  Write: 'Writing a file…',
  Edit: 'Editing files…',
  MultiEdit: 'Editing files…',
  NotebookEdit: 'Editing a notebook…',
  Grep: 'Searching the codebase…',
  Glob: 'Looking for files…',
  WebSearch: 'Searching the web…',
  WebFetch: 'Reading a web page…',
  Task: 'Working on a sub-task…',
  Agent: 'Working on a sub-task…',
  TodoWrite: 'Updating its checklist…',
}

function poolToolLabel(slug: string): string | null {
  if (NATIVE_TOOL_LABELS[slug]) {
    return NATIVE_TOOL_LABELS[slug]
  }
  const s = slug.toLowerCase()
  if (s.includes('ask_user_question') || s.includes('askuserquestion')) {
    return 'Asking you a question…'
  }
  if (s.includes('submit_plan') || s.includes('planmode') || s.includes('propose_plan')) {
    return 'Drafting a plan…'
  }
  if (s.includes('schedule_followup') || s.includes('schedulewakeup')) {
    return 'Scheduling a follow-up…'
  }
  if (s.includes('check_inbox')) {
    return 'Checking your messages…'
  }
  if (s.includes('report_artifacts')) {
    return 'Saving its work…'
  }
  if (s.includes('preview-env') || s.includes('preview_') || s.includes('workspace')) {
    return 'Working in the workspace…'
  }
  return null
}

/**
 * Friendly status string for an in-flight tool call. Reads the slug +
 * args and produces something a user can scan: "Looking up Tasks…",
 * "Creating task 'Mail in district renewal'…", "Looking up skills for
 * 'parent newsletter tone'…". Falls back to the bare slug when no
 * mapping fits — better than crashing on an unexpected new tool.
 */
export function formatToolCallStatus(slug: string, args: unknown): string {
  const brand = brandForSlug(slug)?.brand ?? null
  const action = slug.split('.')[1] ?? slug

  // Skills.search gets a custom render — show the query when present.
  if (slug === 'skills.search') {
    const q = (args as { query?: string } | null)?.query?.trim()
    return q ? `Looking up skills for "${truncate(q, 40)}"…` : 'Looking up skills…'
  }

  // agent.propose_plan
  if (slug === 'agent.propose_plan') {
    return 'Drafting a plan…'
  }

  // Factory-pool harness + native tools (see poolToolLabel).
  const poolLabel = poolToolLabel(slug)
  if (poolLabel) {
    return poolLabel
  }

  // Common tool action patterns. Order matters — most specific first.
  if (action.startsWith('create_')) {
    const title = (args as { title?: string; name?: string } | null)?.title ?? (args as { name?: string } | null)?.name
    const what = action.replace(/^create_/, '').replace(/_/g, ' ')
    return title ? `Creating ${what} "${truncate(title, 40)}"…` : `Creating ${what} in ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('update_')) {
    const what = action.replace(/^update_/, '').replace(/_/g, ' ')
    return `Updating ${what} in ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('complete_')) {
    const what = action.replace(/^complete_/, '').replace(/_/g, ' ')
    return `Completing ${what} in ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('send_')) {
    return `Sending via ${brand ?? 'Communications'}…`
  }
  if (action.startsWith('comment_')) {
    return `Adding comment in ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('attach_')) {
    return `Attaching file in ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('submit_')) {
    return `Submitting form in ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('list_my_') || action === 'list_my') {
    return `Looking up your ${brand ?? 'data'}…`
  }
  if (action.startsWith('list_all_') || action === 'list_all' || action.startsWith('list_team_')) {
    return `Looking up all ${brand ?? 'data'}…`
  }
  if (action.startsWith('list_')) {
    return `Looking up ${brand ?? 'data'}…`
  }
  if (action.startsWith('find_') || action.startsWith('search_')) {
    return `Searching ${brand ?? 'Ordo'}…`
  }
  if (action.startsWith('get_') || action.startsWith('lookup_')) {
    return `Loading detail from ${brand ?? 'Ordo'}…`
  }

  // Fallback: brand-aware generic verb.
  return brand ? `Working with ${brand}…` : `Calling ${slug}…`
}

/**
 * Friendly summary of a tool result, rendered as a suffix on the
 * resolved chip ("Looked up skills · 3 found"). Reads common shapes
 * (`hits`, `tasks`, `count`, `total`) and falls back to nothing.
 */
export function summarizeToolResult(slug: string, result: unknown): string | null {
  if (!result || typeof result !== 'object') {
    return null
  }
  const r = result as Record<string, unknown>

  if (slug === 'skills.search') {
    const hits = Array.isArray(r.hits) ? r.hits.length : null
    return hits === null ? null : hits === 0 ? 'no skills found' : `${hits} skill${hits === 1 ? '' : 's'} found`
  }
  if (slug === 'agent.propose_plan') {
    return null // Plan card itself surfaces the result.
  }
  if (slug.endsWith('.create_task') || slug.endsWith('.create_subtask')) {
    const title = (r as { title?: string }).title ?? (r as { name?: string }).name
    return title ? `created "${truncate(title, 40)}"` : 'created'
  }
  if (slug.endsWith('.complete_task')) {
    return 'marked complete'
  }
  if (slug.endsWith('.update_task')) {
    return 'updated'
  }
  if (slug.endsWith('.send_email_via_template') || slug.endsWith('.send_sms_via_template')) {
    const recipientCount = (r as { recipientCount?: number }).recipientCount
    return typeof recipientCount === 'number' ? `sent to ${recipientCount}` : 'sent'
  }

  // Generic count detection for list tools.
  const count = countFromResult(r)
  if (typeof count === 'number') {
    return count === 0 ? 'none found' : `${count} found`
  }
  return null
}

/**
 * One-line summary of a tool call's args — shown inline as the "remnant" of an
 * approval the user just accepted, so the visible record of "what got run"
 * doesn't disappear after a write tool fires.
 *
 * Tries the common shapes first (`title`, `name`, `query`, `slug`, `subject`,
 * `to`) — those cover the vast majority of read/write tools. Falls back to a
 * compact one-line JSON-ish render for everything else, capped to ~120 chars so
 * it never blows out the conversation column.
 */
export function summarizeToolArgs(args: unknown): string | null {
  if (!args || typeof args !== 'object') {
    return null
  }
  const obj = args as Record<string, unknown>
  for (const key of ['title', 'name', 'query', 'slug', 'subject', 'to']) {
    const v = obj[key]
    if (typeof v === 'string' && v.trim().length > 0) {
      return v.length > 120 ? `${v.slice(0, 120)}…` : v
    }
  }
  try {
    const compact = JSON.stringify(obj)
    if (compact.length <= 2) {
      return null
    }
    return compact.length > 120 ? `${compact.slice(0, 120)}…` : compact
  } catch {
    return null
  }
}

function countFromResult(r: Record<string, unknown>): number | null {
  const candidates = ['count', 'total']
  for (const k of candidates) {
    const v = r[k]
    if (typeof v === 'number') {
      return v
    }
  }
  // Look for first array field with primitive items (likely the result list).
  for (const v of Object.values(r)) {
    if (Array.isArray(v)) {
      return v.length
    }
  }
  return null
}

function truncate(s: string, max: number): string {
  if (s.length <= max) {
    return s
  }
  return s.slice(0, max - 1).trimEnd() + '…'
}
